/**
 * Login Component - Basic HTTP Authentication
 */

import React, { useState, FormEvent } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import LegalLinks from '../Legal/LegalLinks';

const Login: React.FC = () => {
  const { signIn, error } = useAuth();

  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [localError, setLocalError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLocalError('');
    setLoading(true);

    try {
      await signIn(username.trim(), password);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Login failed');
      setLoading(false);
    }
  };

  const displayError = localError || error;

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 bg-[url('/login-bg.webp')] bg-cover bg-center text-white">
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-slate-950/10 to-slate-950/70" aria-hidden="true" />

      <header className="relative mx-auto flex max-w-6xl items-center justify-between px-5 py-5">
        <span className="text-lg font-bold tracking-tight">AI Interview Practice</span>
        <nav className="flex items-center gap-5 text-sm">
          <a href="/demo" className="text-indigo-200 no-underline hover:text-white">
            Free demo
          </a>
          <a href="/pricing" className="text-indigo-200 no-underline hover:text-white">
            Pricing
          </a>
        </nav>
      </header>

      <main className="relative mx-auto grid max-w-6xl items-center gap-10 px-5 pb-12 pt-4 lg:min-h-[calc(100vh-80px)] lg:grid-cols-[1.1fr_1fr] lg:pb-20 lg:pt-0">
        <section className="text-center lg:text-left">
          <p className="mb-4 inline-block rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-indigo-100 ring-1 ring-white/15">
            🎙️ Voice interviews · Live coding · Instant scores
          </p>
          <h1 className="m-0 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            Practise the interview{' '}
            <span className="bg-gradient-to-r from-indigo-300 to-pink-300 bg-clip-text text-transparent">
              before it counts
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-lg text-lg text-indigo-100/90 lg:mx-0">
            An AI interviewer that listens, asks follow-ups and scores every
            answer, from live coding to system design.
          </p>
          <ul className="mx-auto mt-8 hidden max-w-md list-none space-y-3 p-0 text-left text-indigo-50 lg:mx-0 lg:block">
            {[
              'Talk it through out loud, like the real thing',
              'Code in a real editor with a JS runner and React sandbox',
              'Scored feedback with clear next steps after every round',
            ].map((point) => (
              <li key={point} className="flex gap-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-indigo-400/20 text-xs text-indigo-200 ring-1 ring-indigo-300/30">
                  ✓
                </span>
                {point}
              </li>
            ))}
          </ul>
        </section>

        <div className="mx-auto w-full max-w-md rounded-3xl bg-white p-8 text-slate-800 shadow-2xl shadow-indigo-950/50 sm:p-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-1 tracking-tight">
              Welcome back
            </h2>
            <p className="text-slate-500 text-sm">Sign in to continue</p>
          </div>

          {displayError && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-3 px-4 text-red-700 text-sm mb-4">
              {displayError}
            </div>
          )}

          <form onSubmit={handleLogin} className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="username"
                className="text-slate-700 text-sm font-medium"
              >
                Email or username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="you@example.com"
                required
                disabled={loading}
                autoComplete="username"
                className="bg-white border-2 border-slate-200 rounded-lg px-4 py-3.5 text-base text-slate-800 transition-all duration-200 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-3 focus:ring-indigo-200 disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="password"
                className="text-slate-700 text-sm font-medium"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={loading}
                autoComplete="current-password"
                className="bg-white border-2 border-slate-200 rounded-lg px-4 py-3.5 text-base text-slate-800 transition-all duration-200 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-3 focus:ring-indigo-200 disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>

            <button
              type="submit"
              className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg px-6 py-3.5 text-base font-semibold cursor-pointer transition-all duration-200 mt-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none hover:from-indigo-600 hover:to-purple-700 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-200 text-center">
            <a
              href="/demo"
              className="block w-full border-2 border-indigo-500 text-indigo-600 rounded-lg px-6 py-3 text-base font-semibold no-underline transition-all duration-200 hover:bg-indigo-50"
            >
              Try a 10-minute demo, no signup
            </a>
            <p className="mt-4 text-sm text-slate-500">
              New here?{' '}
              <a href="/pricing" className="font-semibold text-indigo-600">
                See plans from £15/month
              </a>
            </p>
          </div>
          <LegalLinks className="mt-6 text-slate-400" />
        </div>
      </main>
    </div>
  );
};

export default Login;
